import { getFiltersMap } from '../Components/ReactDataGrid/SortFilterTables';
import { BuildResultsEndpoint, FilterParam } from '../types';
import buildBaseEndpoint from './buildBaseEndpoint';
import { decodeData } from './encodeUtils';
import buildNumericFilters from './buildNumericFilters'; // <-- Importa tu nueva función

const buildResultEndpoint = ({
  view,
  source,
  tab,
  searchItems,
  isEvidence,
  isMapping,
  mappingContent,
  pageNum,
  pageSize,
  orderByParam,
  order,
  defaultFilterValue,
  sourceWeights,
}: BuildResultsEndpoint) => {
  const url = window.location.search;
  const p = new URLSearchParams(url);
  const filterToken = p.get('f');
  const decodedFilters = decodeData(filterToken as string);

  const relevantFilters =
    decodedFilters && decodedFilters.length > 0
      ? decodedFilters.filter(f => f.isEvidence === isEvidence)
      : [];

  const allFilters = relevantFilters.length > 0 ? relevantFilters : defaultFilterValue;

  // idents to filter mappings
  // The param name:
  let filterParam: FilterParam = null;
  filterParam = view === 'GENES' ? 'gene_ncbi_id' : filterParam;
  filterParam = view === 'DISEASES' ? 'disease' : filterParam;
  filterParam = view === 'VARIANTS' ? 'variant' : filterParam;
  filterParam = view === 'CHEMICALS' ? 'chem_ids_init' : filterParam;
  filterParam = view === 'ALL' ? null : filterParam;

  // The backend idents value:
  let backendDiseaseVocabPrefix = filterParam === 'disease' ? 'UMLS_' : '';
  const filterValue = searchItems!.map(item => backendDiseaseVocabPrefix + item.id).join(',');

  // order mapping
  if (order === 1) {
    order = 'asc';
  } else if (order === -1) {
    order = 'desc';
  }

  // Update orderByParam with custom mappings
  const specialSortParams = [
    'numGenesAssociatedToDisease',
    'numVariantsAssociatedToDisease',
    'numVariantsAssociatedToGene',
    'numDiseasesAssociatedToGene',
    'numDiseasesAssociatedToVariant',
    'numPMIDsupportingAssociation',
    'numDBSNPsupportingAssociation',
    'numPublications',
  ];
  if (specialSortParams.includes(orderByParam)) {
    orderByParam = `${orderByParam}_${source}`;
  }
  if (['yearInitial', 'yearFinal'].includes(orderByParam) && (tab === 'GDA' || tab === 'VDA')) {
    orderByParam = orderByParam + '_' + source;
  }

  // Handle specific naming inconsistencies or mappings
  switch (orderByParam) {
    case 'chemical_effect':
      orderByParam = 'tox_ther';
      break;
    case 'diseaseClassName':
      orderByParam = orderByParam.replace('_', '');
      break;
    case 'ddaRelation':
      orderByParam = 'rela';
      break;
    case 'symbol':
      orderByParam = 'symbol_keyword';
      break;
    case 'chemsInSentence':
      orderByParam = 'chemPrefName';
      break;
    default:
      break;
  }

  // Build params
  const paramsMap = {
    source: source ?? '',
    page_number: String(pageNum),
    page_size: String(pageSize),
    order_by: orderByParam ?? '',
    order: order != null ? String(order) : '',
  };

  // Apply user selected filters
  if (isMapping && mappingContent) {
    if (mappingContent.mappingItems) {
      mappingContent.mappingItems.forEach(item => {
        backendDiseaseVocabPrefix = item.filterParam === 'disease' ? 'UMLS_' : '';
        if (item.filterParam && paramsMap[item.filterParam]) {
          paramsMap[item.filterParam] += ',' + backendDiseaseVocabPrefix + item.filterValue;
        } else if (item.filterParam) {
          paramsMap[item.filterParam] = backendDiseaseVocabPrefix + item.filterValue;
        }
      });
    } else {
      console.error('No mappingItems provided for mappingContent');
      return null;
    }
  } else {
    paramsMap[filterParam as string] = filterValue;
  }

  // Apply string filters from ReactDataGrid
  allFilters.forEach(filter => {
    if (filter.value && ['string', 'select'].includes(filter.type)) {
      const map = getFiltersMap().get(filter.name) ?? filter.name;
      if (filter.type === 'string' && filter.operator === 'contains') {
        paramsMap[map] = `contains_${filter.value}`;
      } else {
        paramsMap[map] = filter.value;
      }
    }
  });

  // Apply number filters from ReactDataGrid
  const numericFilters = buildNumericFilters(allFilters);
  Object.assign(paramsMap, numericFilters);

  // Construct URL params
  const params = new URLSearchParams(
    Object.entries(paramsMap)
      .filter(([_param, value]) => value !== (null as unknown))
      .filter(([_param, value]) => value !== ('' as unknown))
      .filter(([param, _value]) => param !== ('null' as unknown)),
  );

  // Construct endpoint URL
  const baseEndpoint = buildBaseEndpoint({
    view,
    tab,
    isEvidence,
    isMapping,
    mappingContent,
  });

  let endPoint = `${baseEndpoint}?${params.toString()}`;

  if (sourceWeights && sourceWeights.length > 0) {
    const weightsString = sourceWeights.map(sw => `${sw.name}:${sw.score}`).join(',');
    endPoint += `&source_weights=${weightsString}`;
  }

  return endPoint;
};

export default buildResultEndpoint;
